import React from "react";
import { usePromoModal } from "../../hooks/ui/usePromoModal";
import DiscountBadge from "./DiscountBadge";
import PromoIconCards from "./PromoIconCards";

export interface PromoModalContent {
  enabled?: boolean;
  title?: string;
  subtitle?: string;
  description?: string;
  buttonText?: string;
  buttonLink?: string;
  imageUrl?: string;
  discountBadge?: string;
  showOnFirstVisit?: boolean;
  showDelay?: number;
  icon1Url?: string;
  icon2Url?: string;
}

export interface PromoModalProps {
  content: PromoModalContent;
}

export const PromoModal: React.FC<PromoModalProps> = ({ content }) => {
  const { isOpen, isClosing, handleClose, handleButtonClick } = usePromoModal(content);

  if (!content.enabled || !isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-gradient-to-br from-black/60 via-black/50 to-black/60 backdrop-blur-md z-[10000] transition-opacity duration-500 ${isClosing ? "opacity-0" : "opacity-100"}`}
        onClick={handleClose}
      />

      {/* Sparkles */}
      <div className="fixed inset-0 z-[10000] pointer-events-none overflow-hidden">
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, animationDelay: `${Math.random() * 2}s`, animationDuration: `${4 + Math.random() * 2}s` }}
          >
            <div className={`w-1.5 h-1.5 rounded-full ${i % 3 === 0 ? "bg-brand/30" : i % 3 === 1 ? "bg-[#d4a574]/40" : "bg-border/50"}`} />
          </div>
        ))}
      </div>

      {/* Modal */}
      <div className={`fixed inset-0 z-[10001] flex items-center justify-center p-4 pointer-events-none ${isClosing ? "opacity-0 scale-95 translate-y-4" : "opacity-100 scale-100 translate-y-0"} transition-all duration-500 ease-out`}>
        <div
          className="relative bg-surface-light rounded-3xl shadow-2xl max-w-md w-full pointer-events-auto overflow-hidden border border-border"
          onClick={(e) => e.stopPropagation()}
          dir="rtl"
        >
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-5 left-5 z-20 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white transition-all duration-200 shadow-lg border border-border hover:scale-110 active:scale-95 group"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5 text-ink-muted group-hover:text-ink-dark transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={3}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {content.discountBadge && <DiscountBadge text={content.discountBadge} />}

          <div className="relative z-10 p-8 pt-16 pb-8">
            <PromoIconCards icon1Url={content.icon1Url} icon2Url={content.icon2Url} />

            {content.title && (
              <h2 className="menu-font text-2xl md:text-3xl text-center mb-3 font-bold tracking-tight leading-snug">{content.title}</h2>
            )}
            {content.subtitle && (
              <p className="text-center text-base md:text-lg text-ink-muted mb-3 leading-relaxed">{content.subtitle}</p>
            )}
            {content.description && (
              <p className="text-center text-sm text-ink-muted/80 mb-8 leading-relaxed">{content.description}</p>
            )}

            {content.buttonText && (
              <div className="flex justify-center">
                <button
                  onClick={handleButtonClick}
                  className="btn-brown px-10 py-4 text-base font-bold hover:scale-105 active:scale-95 transition-all duration-300 shadow-[0_8px_25px_rgba(116,66,29,0.3)] hover:shadow-[0_12px_35px_rgba(116,66,29,0.4)]"
                >
                  {content.buttonText}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default PromoModal;
